/**
 * ============================================================================
 * OBJECTS STORE - UPDATE OPERATIONS
 * ============================================================================
 * 
 * Handles updating existing objects via the backend API.
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on failure
 * - Version bump support (patch, minor, major)
 * - Change summary tracking
 * 
 * @module objects/api/update
 */

import { apiService } from "@/lib/api-functions/api-service";
import { processObjectForTranslation } from "@/lib/utils/objectParser";
import { createString, updateStringsItem } from "../../stringsStore";
import { objectsStore } from "../store";
import type { UpdateObjectData, ObjectItem, ToastFunction } from "../types";

// ============================================================================
// UPDATE OPERATIONS
// ============================================================================

/**
 * Update an existing object via API and sync the store.
 * 
 * Features:
 * - Performs optimistic update for instant UI feedback
 * - Automatically rolls back on API failure
 * - Processes translatable fields
 * - Supports version bumping
 * 
 * @param item - The object data to update (must include id)
 * @param versionBump - Version bump type: 'patch' (default), 'minor', or 'major'
 * @param changeSummary - Optional description of the changes
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the updated object
 * 
 * @example
 * ```ts
 * // Simple update with patch version bump
 * const updated = await updateObject(objectData);
 * 
 * // Update with minor version bump and change summary
 * const updated = await updateObject(
 *   objectData,
 *   'minor',
 *   'Added new abilities section'
 * );
 * ```
 */
export async function updateObject(
  item: UpdateObjectData,
  versionBump: 'patch' | 'minor' | 'major' = 'patch',
  changeSummary?: string,
  toast?: ToastFunction
): Promise<ObjectItem> {
  // Store the current state for potential rollback
  const currentData = objectsStore.getData();
  const existingItem = currentData.find(obj => obj.id === item.id);
  
  // ========================================================================
  // OPTIMISTIC UPDATE
  // ========================================================================
  // Apply changes immediately for instant UI feedback
  if (existingItem) {
    const optimisticItem: ObjectItem = {
      ...existingItem,
      ...item,
      updated_at: new Date().toISOString(),
    };
    
    const optimisticData = currentData.map(obj => 
      obj.id === item.id ? optimisticItem : obj
    );
    objectsStore.setData(optimisticData);
  }

  try {
    // ======================================================================
    // PROCESS TRANSLATABLE FIELDS
    // ======================================================================
    const { glossaryItem: processedItem } = await processObjectForTranslation(
      item,
      createString,
      updateStringsItem
    );
    
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // SEND TO API
    // ======================================================================
    const result = await apiService.updateObject({
      ...processedItem,
      versionBump,
      changeSummary
    });

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update object');
    }

    const updatedItem = result.data;

    // ======================================================================
    // SYNC STORE WITH SERVER RESPONSE
    // ======================================================================
    // Replace optimistic data with actual server response
    const finalData = objectsStore.getData();
    const updatedData = finalData.map(obj => 
      obj.id === item.id ? updatedItem : obj
    );
    objectsStore.setData(updatedData);

    console.log(`[updateObject] Updated object: ${updatedItem.id} - ${updatedItem.name}`);
    return updatedItem;
    
  } catch (err) {
    // ======================================================================
    // ROLLBACK ON FAILURE
    // ======================================================================
    // Revert the optimistic update
    objectsStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to update object';
    console.error('[updateObject] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
